type ViteEnv = { env: Record<string, string | undefined> };

const ADMIN_API_BASE: string =
  (import.meta as unknown as ViteEnv).env.VITE_ADMIN_API_BASE ?? "";

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${ADMIN_API_BASE}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      "content-type": "application/json",
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    let detail = "";
    try {
      const body = (await res.json()) as { detail?: string };
      detail = body.detail ?? "";
    } catch {
      detail = await res.text();
    }
    throw new ApiError(res.status, detail || res.statusText);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export type AuthConfigResponse = {
  enabled: boolean;
  google_client_id: string;
  allowed_email_domain: string;
};

export type AuthMeResponse = {
  user: {
    email: string;
    name: string;
    picture?: string | null;
  } | null;
  auth_required: boolean;
};

export type GoogleLoginResponse = {
  user: {
    email: string;
    name: string;
    picture?: string | null;
  };
};

export const api = {
  authConfig: () => request<AuthConfigResponse>("/auth/config"),
  me: () => request<AuthMeResponse>("/auth/me"),
  googleLogin: (code: string) =>
    request<GoogleLoginResponse>("/auth/google", {
      method: "POST",
      body: JSON.stringify({ code }),
    }),
  logout: () => request<{ message: string }>("/auth/logout", { method: "POST" }),
  funnel: (params: { slug: string; from: string; to: string; questionIds: string[] }) => {
    const query = new URLSearchParams({
      slug: params.slug,
      from: params.from,
      to: params.to,
      question_ids: params.questionIds.join(","),
    });
    return request<FunnelResponse>(`/api/admin/analytics/funnel?${query.toString()}`);
  },
  submissions: (params: {
    slug?: string;
    persona?: string;
    page?: number;
    pageSize?: number;
  }) => {
    const query = new URLSearchParams();
    if (params.slug) query.set("slug", params.slug);
    if (params.persona) query.set("persona", params.persona);
    if (params.page) query.set("page", String(params.page));
    if (params.pageSize) query.set("page_size", String(params.pageSize));
    const qs = query.toString();
    return request<SubmissionsResponse>(
      `/api/admin/submissions${qs ? `?${qs}` : ""}`,
    );
  },
};

export type FunnelStep = {
  key: string;
  label: string;
  question_id: string | null;
  viewed: number;
  answered: number;
  drop_off_pct: number | null;
};

export type FunnelResponse = {
  quiz_slug: string;
  started: number;
  completed: number;
  completion_rate: number | null;
  avg_duration_ms: number | null;
  steps: FunnelStep[];
};

export type SubmissionRow = {
  id: number;
  quiz_slug: string;
  persona: string;
  email: string | null;
  phone: string | null;
  klaviyo_synced_at: string | null;
  klaviyo_error: string | null;
  created_at: string;
  intelligems_variant: string | null;
  session_id: string | null;
};

export type SubmissionsResponse = {
  total: number;
  items: SubmissionRow[];
  page: number;
  page_size: number;
};
